/**
 * Scrittori dell'export Statistiche SLA: **PDF** (jsPDF) e **Excel** (xlsx).
 *
 * Perche' due librerie e non una: il pattern di export gia' presente nel client e'
 * `utils/processExport.ts` (jsPDF per il PDF, CSV con Blob per il tabellare) e la
 * cartella `package.json` ha gia' `xlsx` (usata da `FilePreviewDialog.tsx:26`).
 * Qui si **riusa** quel pattern invece di inventarne uno: stesso download con
 * `Blob` + ancora (`processExport.ts:182-195`), stesso slug del nome file
 * (`processExport.ts:176-180`), nessuna dipendenza nuova.
 *
 * Differenza dichiarata rispetto al CSV di `processExport`: l'Excel e' un `.xlsx`
 * **reale**, quindi i conteggi restano celle **numeriche** (Excel puo' sommarli) e
 * solo le percentuali localizzate sono testo — e' la stessa distinzione che il
 * test Python pretende dai documenti P4 (`celle Excel realmente numeriche`).
 *
 * Limite dichiarato del PDF: i font standard di jsPDF usano la codifica WinAnsi,
 * che copre le sei lingue del catalogo (it/en/es/fr/de/pt, tutte latine) ma non i
 * simboli tipografici. `pdfSafeText` sostituisce quei pochi caratteri con
 * equivalenti ASCII **nel solo PDF**: e' una conversione di codifica, non una
 * traduzione, e non tocca il testo del catalogo ne' l'Excel (che resta fedele).
 */

import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import type {
  SlaStatisticsExportCell,
  SlaStatisticsExportDocument,
} from "model/slaStatisticsExportModel";

/** Cella Excel: numero se il documento porta un numero, testo altrimenti. */
const asSheetCell = (cell: SlaStatisticsExportCell): SlaStatisticsExportCell =>
  typeof cell === "number" && Number.isFinite(cell) ? cell : String(cell ?? "");

/**
 * Neutralizzazione delle formule: un valore che inizia con `=`, `+`, `-` o `@`
 * puo' essere interpretato come formula. I valori dell'export includono dati
 * inseriti dall'utente (il valore Cliente del filtro, `customervalues`), quindi la
 * cella viene prefissata con un apostrofo come prescrive la pratica dei fogli di
 * calcolo. Nessun valore viene alterato oltre il prefisso.
 */
const neutralized = (value: SlaStatisticsExportCell): SlaStatisticsExportCell =>
  typeof value === "string" && /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

/** Righe del foglio: titolo, metadati, legenda, tabelle. Una sola verita'. */
export const slaStatisticsExportSheetRows = (
  document: SlaStatisticsExportDocument,
): SlaStatisticsExportCell[][] => {
  const rows: SlaStatisticsExportCell[][] = [[document.title], []];
  document.metadata.forEach((row) => rows.push([row.label, neutralized(row.value)]));
  if (document.legend.length > 0) {
    rows.push([]);
    document.legend.forEach((entry) => rows.push([neutralized(entry)]));
  }
  document.tables.forEach((table) => {
    rows.push([]);
    rows.push([table.title]);
    rows.push(table.columns.map(neutralized));
    table.rows.forEach((row) => rows.push(row.map((cell) => neutralized(asSheetCell(cell)))));
  });
  return rows;
};

/** Download di un Blob con l'ancora, stesso pattern di `processExport.ts:182-195`. */
const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * Excel `.xlsx`: un foglio unico con metadati e tabelle. La larghezza delle colonne
 * e' dichiarata perche' i metadati sono label/valore su due colonne e le tabelle
 * hanno fino a quattro colonne.
 */
export const buildSlaStatisticsExcel = (
  exportDocument: SlaStatisticsExportDocument,
): Blob => {
  const sheet = XLSX.utils.aoa_to_sheet(slaStatisticsExportSheetRows(exportDocument));
  sheet["!cols"] = [{ wch: 32 }, { wch: 28 }, { wch: 16 }, { wch: 24 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "SLA");
  const output = XLSX.write(book, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  return new Blob([output], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

/**
 * Sostituzione dei caratteri non rappresentabili in WinAnsi con equivalenti ASCII.
 * Vale **solo** per il PDF (font standard di jsPDF): l'Excel e il DOM restano
 * fedeli al testo del catalogo.
 */
export const pdfSafeText = (value: string): string =>
  value
    .replace(/→/g, "->")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...");

/** PDF: titolo, metadati, legenda e tabelle, con a capo e pagine. */
export const buildSlaStatisticsPdf = (
  exportDocument: SlaStatisticsExportDocument,
): Blob => {
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  let y = 18;

  doc.setFontSize(14);
  doc.text(pdfSafeText(exportDocument.title), margin, y);
  y += 8;
  doc.setFontSize(9);

  const line = (text: string, size = 9) => {
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(pdfSafeText(text), pageWidth - margin * 2) as string[];
    lines.forEach((entry) => {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(entry, margin, y);
      y += 5;
    });
  };

  exportDocument.metadata.forEach((row) => line(`${row.label}: ${row.value}`));

  if (exportDocument.legend.length > 0) {
    y += 4;
    exportDocument.legend.forEach((entry) => line(entry, 8));
  }

  exportDocument.tables.forEach((table) => {
    y += 6;
    line(table.title, 11);
    const columnWidth = Math.max(
      28,
      (pageWidth - margin * 2) / Math.max(table.columns.length, 1),
    );
    const rowCells = (cells: SlaStatisticsExportCell[]) =>
      cells.map(
        (cell) =>
          (doc.splitTextToSize(
            pdfSafeText(String(cell ?? "")),
            columnWidth - 2,
          ) as string[]),
      );
    const drawRow = (cells: string[][]) => {
      const height = Math.max(...cells.map((cell) => cell.length), 1) * 4 + 2;
      if (y + height > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      cells.forEach((cell, index) => {
        doc.text(cell, margin + index * columnWidth, y);
      });
      y += height;
    };
    doc.setFont("helvetica", "bold");
    drawRow(rowCells(table.columns));
    doc.setFont("helvetica", "normal");
    table.rows.forEach((row) => drawRow(rowCells(row)));
  });

  return doc.output("blob");
};

/**
 * Scarica il documento nel formato richiesto. Solleva l'errore del writer: la
 * gestione **non silenziosa** sta nel comando (`SlaStatisticsExportMenu`), che lo
 * rende con una chiave del catalogo.
 */
export const downloadSlaStatisticsExport = (
  exportDocument: SlaStatisticsExportDocument,
  format: "pdf" | "excel",
): void => {
  if (!exportDocument.available) {
    throw new Error("documento di export senza dati");
  }
  const blob =
    format === "excel"
      ? buildSlaStatisticsExcel(exportDocument)
      : buildSlaStatisticsPdf(exportDocument);
  downloadBlob(blob, exportDocument.fileName);
};
